import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  Edit3, Save, Plus, Trash2, User, Briefcase, GraduationCap, 
  Award, FileText, Loader2, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthToken } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EditableResumePreview = ({ 
  resumeId, 
  initialData, 
  onSave, 
  onDataChange,
  source = "refine", // "refine" or "template"
  templateId = null
}) => {
  const [data, setData] = useState(initialData || {});
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (initialData) {
      setData(initialData);
    }
  }, [initialData]);

  const updateField = (field, value) => {
    const newData = { ...data, [field]: value };
    setData(newData);
    setHasChanges(true);
    if (onDataChange) onDataChange(newData);
  };

  const updateExperience = (index, field, value) => {
    const newExp = [...(data.experience || [])];
    newExp[index] = { ...newExp[index], [field]: value };
    updateField('experience', newExp);
  };

  const updateExperienceAchievement = (expIndex, achIndex, value) => {
    const newExp = [...(data.experience || [])];
    const achievements = [...(newExp[expIndex].achievements || [])];
    achievements[achIndex] = value;
    newExp[expIndex] = { ...newExp[expIndex], achievements };
    updateField('experience', newExp);
  };

  const addExperienceAchievement = (expIndex) => {
    const newExp = [...(data.experience || [])];
    const achievements = [...(newExp[expIndex].achievements || []), ""];
    newExp[expIndex] = { ...newExp[expIndex], achievements };
    updateField('experience', newExp);
  };

  const removeExperienceAchievement = (expIndex, achIndex) => {
    const newExp = [...(data.experience || [])];
    const achievements = newExp[expIndex].achievements.filter((_, i) => i !== achIndex);
    newExp[expIndex] = { ...newExp[expIndex], achievements };
    updateField('experience', newExp);
  };

  const addExperience = () => {
    const newExp = [...(data.experience || []), { 
      job_title: "", 
      company: "", 
      dates: "", 
      achievements: [""] 
    }];
    updateField('experience', newExp);
  };

  const removeExperience = (index) => {
    const newExp = data.experience.filter((_, i) => i !== index);
    updateField('experience', newExp);
  };

  const updateEducation = (index, field, value) => {
    const newEdu = [...(data.education || [])];
    newEdu[index] = { ...newEdu[index], [field]: value };
    updateField('education', newEdu);
  };

  const addEducation = () => {
    const newEdu = [...(data.education || []), { degree: "", university: "", year: "" }];
    updateField('education', newEdu);
  };

  const removeEducation = (index) => {
    const newEdu = data.education.filter((_, i) => i !== index);
    updateField('education', newEdu);
  };

  const updateSkill = (index, value) => {
    const newSkills = [...(data.skills || [])];
    newSkills[index] = value;
    updateField('skills', newSkills);
  };

  const addSkill = () => {
    const newSkills = [...(data.skills || []), ""];
    updateField('skills', newSkills);
  };

  const removeSkill = (index) => {
    const newSkills = data.skills.filter((_, i) => i !== index);
    updateField('skills', newSkills);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Get auth token if available (works for both signed-in and anonymous users)
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/resumes/save-edits`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          resume_id: resumeId,
          edited_data: data,
          source: source,
          template_id: templateId
        }),
      });

      if (response.ok) {
        toast.success('Changes saved successfully!');
        setHasChanges(false);
        setIsEditing(false);
        if (onSave) onSave(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to save changes');
      }
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3 border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            {isEditing ? 'Edit Your Resume' : 'Resume Preview'}
          </CardTitle>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => { setIsEditing(false); setData(initialData); setHasChanges(false); }}
                >
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Save Changes
                </Button>
              </>
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsEditing(true)}
                data-testid="edit-resume-btn"
              >
                <Edit3 className="h-4 w-4 mr-1" />
                Edit Resume
              </Button>
            )}
          </div>
        </div>
        {hasChanges && (
          <Badge className="mt-2 bg-amber-100 text-amber-700">Unsaved changes</Badge>
        )}
      </CardHeader>

      <CardContent className="p-6">
        <ScrollArea className="h-[600px] pr-4">
          {/* Personal Info Section */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 mb-3 text-primary font-medium">
              <User className="h-4 w-4" />
              Personal Information
            </div>
            {isEditing ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Full Name</label>
                  <Input 
                    value={data.full_name || ''} 
                    onChange={(e) => updateField('full_name', e.target.value)}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Professional Title</label>
                  <Input 
                    value={data.professional_title || ''} 
                    onChange={(e) => updateField('professional_title', e.target.value)}
                    placeholder="Software Engineer"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Email</label>
                  <Input 
                    value={data.email || ''} 
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="john@email.com"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Phone</label>
                  <Input 
                    value={data.phone || ''} 
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground">Location</label>
                  <Input 
                    value={data.location || ''} 
                    onChange={(e) => updateField('location', e.target.value)}
                    placeholder="New York, NY"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center">
                <h2 className="text-xl font-bold text-primary">{data.full_name || 'Your Name'}</h2>
                <p className="text-blue-600">{data.professional_title || 'Professional Title'}</p>
                <p className="text-sm text-muted-foreground">
                  {data.location} {data.email && `| ${data.email}`} {data.phone && `| ${data.phone}`}
                </p>
              </div>
            )}
          </div>

          {/* Summary Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2 text-primary font-medium text-sm uppercase">
              Professional Summary
            </div>
            {isEditing ? (
              <Textarea 
                value={data.summary || ''} 
                onChange={(e) => updateField('summary', e.target.value)}
                placeholder="Brief professional summary..."
                rows={3}
              />
            ) : (
              <p className="text-sm text-slate-600">{data.summary || 'No summary provided'}</p>
            )}
          </div>

          {/* Experience Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-primary font-medium text-sm uppercase">
                <Briefcase className="h-4 w-4" />
                Experience
              </div>
              {isEditing && (
                <Button variant="ghost" size="sm" onClick={addExperience}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              )}
            </div>
            {(data.experience || []).map((exp, expIndex) => (
              <div key={expIndex} className="mb-4 p-3 bg-slate-50 rounded-lg">
                {isEditing ? (
                  <>
                    <div className="flex justify-between mb-2">
                      <span className="text-xs text-muted-foreground">Job {expIndex + 1}</span>
                      <Button variant="ghost" size="sm" className="h-6 text-red-500" onClick={() => removeExperience(expIndex)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Input 
                        value={exp.job_title || ''} 
                        onChange={(e) => updateExperience(expIndex, 'job_title', e.target.value)}
                        placeholder="Job Title"
                      />
                      <Input 
                        value={exp.company || ''} 
                        onChange={(e) => updateExperience(expIndex, 'company', e.target.value)}
                        placeholder="Company"
                      />
                    </div>
                    <Input 
                      value={exp.dates || ''} 
                      onChange={(e) => updateExperience(expIndex, 'dates', e.target.value)}
                      placeholder="Jan 2020 - Present"
                      className="mb-2"
                    />
                    <div className="pl-2">
                      <label className="text-xs text-muted-foreground">Achievements</label>
                      {(exp.achievements || []).map((ach, achIndex) => (
                        <div key={achIndex} className="flex gap-1 mb-1">
                          <Input 
                            value={ach} 
                            onChange={(e) => updateExperienceAchievement(expIndex, achIndex, e.target.value)}
                            placeholder="Achievement..."
                          />
                          <Button variant="ghost" size="sm" className="h-9 text-red-500" onClick={() => removeExperienceAchievement(expIndex, achIndex)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" className="text-xs" onClick={() => addExperienceAchievement(expIndex)}>
                        <Plus className="h-3 w-3 mr-1" /> Add Achievement
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium">{exp.job_title}</div>
                    <div className="text-blue-600 text-sm">{exp.company}</div>
                    <div className="text-xs text-muted-foreground mb-2">{exp.dates}</div>
                    <ul className="text-sm text-slate-600 list-disc pl-4">
                      {(exp.achievements || []).map((ach, i) => (
                        <li key={i}>{ach}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Skills Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-primary font-medium text-sm uppercase">
                Skills
              </div>
              {isEditing && (
                <Button variant="ghost" size="sm" onClick={addSkill}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              )}
            </div>
            {isEditing ? (
              <div className="flex flex-wrap gap-2">
                {(data.skills || []).map((skill, index) => (
                  <div key={index} className="flex items-center gap-1">
                    <Input 
                      value={skill} 
                      onChange={(e) => updateSkill(index, e.target.value)}
                      className="w-32 h-8"
                      placeholder="Skill"
                    />
                    <Button variant="ghost" size="sm" className="h-8 text-red-500" onClick={() => removeSkill(index)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(data.skills || []).map((skill, index) => (
                  <Badge key={index} variant="secondary">{skill}</Badge>
                ))}
              </div>
            )}
          </div>

          {/* Education Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-primary font-medium text-sm uppercase">
                <GraduationCap className="h-4 w-4" />
                Education
              </div>
              {isEditing && (
                <Button variant="ghost" size="sm" onClick={addEducation}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              )}
            </div>
            {(data.education || []).map((edu, index) => (
              <div key={index} className="mb-2 p-3 bg-slate-50 rounded-lg">
                {isEditing ? (
                  <>
                    <div className="flex justify-between mb-2">
                      <span className="text-xs text-muted-foreground">Education {index + 1}</span>
                      <Button variant="ghost" size="sm" className="h-6 text-red-500" onClick={() => removeEducation(index)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input 
                        value={edu.degree || ''} 
                        onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                        placeholder="Degree"
                      />
                      <Input 
                        value={edu.university || ''} 
                        onChange={(e) => updateEducation(index, 'university', e.target.value)}
                        placeholder="University"
                      />
                      <Input 
                        value={edu.year || ''} 
                        onChange={(e) => updateEducation(index, 'year', e.target.value)}
                        placeholder="Year"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium">{edu.degree}</div>
                    <div className="text-sm text-muted-foreground">{edu.university} | {edu.year}</div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Certifications Section */}
          {(data.certifications?.length > 0 || isEditing) && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-primary font-medium text-sm uppercase">
                  <Award className="h-4 w-4" />
                  Certifications
                </div>
              </div>
              {isEditing ? (
                <Textarea 
                  value={(data.certifications || []).join('\n')} 
                  onChange={(e) => updateField('certifications', e.target.value.split('\n').filter(c => c.trim()))}
                  placeholder="One certification per line..."
                  rows={3}
                />
              ) : (
                <ul className="text-sm text-slate-600 list-disc pl-4">
                  {(data.certifications || []).map((cert, index) => (
                    <li key={index}>{cert}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default EditableResumePreview;
